import { Stack, Typography, useTheme } from "@mui/material";
import _ from "lodash";
import { useCallback, useMemo } from "react";
import { Accordion, AccordionTitle } from "src/components";
import { UserFeedbackFilterByOption, UserFeedbackGroupByOption } from "src/constants";
import { type UserFeedbackDto } from "src/http";
import { UserFeedbackFilterButton } from "./UserFeedbackFilterButton";
import { UserFeedbackListItem } from "./UserFeedbackListItem";
import { UserFeedbackListSkeleton } from "./UserFeedbackListSkeleton";

interface IProps {
  title: string;
  items: UserFeedbackDto[];
  groupBy: UserFeedbackGroupByOption;
  filters: Record<UserFeedbackFilterByOption, string[]>;
  onGroupByChanged: (value: UserFeedbackGroupByOption) => void;
  onFilterByChanged: (filters: Record<UserFeedbackFilterByOption, string[]>) => void;
}

const UserFeedbackList = (props: IProps) => {
  const theme = useTheme();

  const groupByFunction = useCallback(
    (item: UserFeedbackDto) => {
      switch (props.groupBy) {
        case UserFeedbackGroupByOption.None: {
          return "All";
        }
        case UserFeedbackGroupByOption.Kind: {
          return ["Performance", "Peer to Peer", "Self-Reflection"][item.type - 1];
        }
        case UserFeedbackGroupByOption.Sender: {
          return item.givenByUser.displayName;
        }
      }
    },
    [props.groupBy],
  );

  const groupSortByFunction = useCallback((groupName: string) => {
    return groupName;
  }, []);

  const groupedItems = useMemo<Map<string, UserFeedbackDto[]> | undefined>(() => {
    if (props.items === undefined) {
      return undefined;
    }

    if (props.items !== undefined) {
      const groupedItems = _.groupBy(props.items, groupByFunction);
      const sortedGroupNames = _.sortBy(_.keys(groupedItems), groupSortByFunction);
      const map = sortedGroupNames.reduce((groupedSortedItems, groupName) => {
        groupedSortedItems.set(groupName, groupedItems[groupName]);
        return groupedSortedItems;
      }, new Map());

      return map;
    }
  }, [groupByFunction, groupSortByFunction, props.items]);

  if (groupedItems === undefined) {
    return <UserFeedbackListSkeleton />;
  }

  return (
    <Stack spacing={0.5} sx={{ p: 0.5 }}>
      <Stack direction="row" spacing={1} sx={{ justifyContent: "space-between" }}>
        <Typography variant="h6" sx={{ pl: 0.75 }}>
          {props.title}
        </Typography>
        <UserFeedbackFilterButton
          groupBy={props.groupBy}
          filters={props.filters}
          onGroupByChanged={props.onGroupByChanged}
          onFilterByChanged={props.onFilterByChanged}
        />
      </Stack>
      <Stack spacing={1}>
        {groupedItems.size === 0 ? (
          <Typography variant="placeholder" sx={{ px: 0.5 }}>
            Nothing here yet.
          </Typography>
        ) : (
          Array.from(groupedItems.keys()).map((groupName, i) => (
            <Accordion key={i} title={<AccordionTitle title={groupName} />} backgroundColor={theme.palette.grey[100]}>
              <Stack spacing={0.5} sx={{ px: 0.5, pb: 0.5 }}>
                {(groupedItems.get(groupName) ?? [])
                  .sort((a, b) => {
                    return (
                      b.submittedDateTimeUtc.localeCompare(a.submittedDateTimeUtc) ||
                      b.createdDateTimeUtc.localeCompare(a.createdDateTimeUtc)
                    );
                  })
                  .map((feedback, i) => (
                    <UserFeedbackListItem key={i} feedback={feedback} />
                  ))}
              </Stack>
            </Accordion>
          ))
        )}
      </Stack>
    </Stack>
  );
};

export { UserFeedbackList };
